import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Building2, Check, ChevronDown } from 'lucide-react';

import { useAuth } from '../lib/auth/AuthProvider';
import { useCompanySwitch } from '../lib/useCompanySwitch';
import { roleLabel } from '../lib/roles';
import { t } from '../lib/i18n';
import { Spinner } from './ui';

/**
 * Switcher de empresa para la topbar (desktop). Si el usuario pertenece a una
 * sola empresa, muestra el nombre estático (igual que antes). Con >1, un
 * dropdown "Trabajando en: [Empresa] ▾" para cambiar sin desloguear.
 */
export function CompanySwitcher({ onSwitched }: { onSwitched?: (name: string) => void }) {
  const { company, companies } = useAuth();
  const { switchingId, run } = useCompanySwitch(onSwitched);

  if (!company) return null;

  // Una sola empresa: nombre estático (se preserva el look previo de la topbar).
  if (companies.length <= 1) {
    return (
      <>
        <span className="text-line">/</span>
        <span className="truncate font-medium text-ink" title={company.name}>
          {company.name}
        </span>
      </>
    );
  }

  return (
    <>
      <span className="text-line">/</span>
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            type="button"
            className="flex min-w-0 items-center gap-1.5 rounded-token px-2 py-1 text-left transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            <span className="hidden font-mono text-[10px] uppercase tracking-[0.14em] text-muted lg:inline">
              {t('company.switch.workingIn')}
            </span>
            <span className="truncate font-medium text-ink" title={company.name}>
              {company.name}
            </span>
            <ChevronDown size={14} className="shrink-0 text-muted" aria-hidden />
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            align="start"
            sideOffset={6}
            className="z-50 min-w-[15rem] max-w-[20rem] rounded-token border border-line bg-surface p-1 shadow-lg"
          >
            <p className="px-3 py-2 font-mono text-[11px] uppercase tracking-[0.14em] text-muted">
              {t('company.switch.title')}
            </p>
            <DropdownMenu.Separator className="my-1 h-px bg-line" />
            {companies.map((c) => {
              const isCurrent = c.id === company.id;
              const isBusy = switchingId === c.id;
              return (
                <DropdownMenu.Item
                  key={c.id}
                  disabled={isCurrent || switchingId !== null}
                  onSelect={(e) => {
                    e.preventDefault();
                    void run(c);
                  }}
                  className="flex cursor-pointer items-center gap-2.5 rounded-[calc(var(--radius)-6px)] px-3 py-2 text-sm text-ink outline-none data-[highlighted]:bg-surface-2 data-[highlighted]:text-brand data-[disabled]:cursor-default"
                >
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-token bg-brand/10 text-brand">
                    <Building2 size={15} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-medium">{c.name}</span>
                    <span className="block truncate text-xs text-muted">{roleLabel(c.role)}</span>
                  </span>
                  {isBusy ? (
                    <Spinner className="h-4 w-4 shrink-0" />
                  ) : isCurrent ? (
                    <Check size={16} className="shrink-0 text-brand" aria-hidden />
                  ) : null}
                </DropdownMenu.Item>
              );
            })}
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </>
  );
}
